<script setup lang="ts">
/**
 * Ручная коррекция 4 углов листа.
 *
 * Показывается, когда jscanify не нашёл углы автоматически (плохое освещение,
 * мятый договор, контрастный фон отсутствует). Пользователь видит исходное
 * фото и 4 точки по углам, которые перетаскивает пальцем.
 *
 * После «Готово» вызываем jscanify.extractPaper(canvas, W, H, cornerPoints) —
 * передаём свои углы, и jscanify делает perspective warp.
 */
import { computed, onMounted, ref, watch } from 'vue';

interface Corner {
  x: number;
  y: number;
}

const props = defineProps<{
  /** Исходный canvas с фото (НЕ выпрямлено) */
  sourceCanvas: HTMLCanvasElement;
}>();

const emit = defineEmits<{
  /** Пользователь подтвердил углы — отдаём в формате jscanify cornerPoints */
  confirmed: [corners: {
    topLeftCorner: Corner;
    topRightCorner: Corner;
    bottomRightCorner: Corner;
    bottomLeftCorner: Corner;
  }];
  cancelled: [];
}>();

// 4 угла в координатах ИСХОДНОГО canvas (пиксели)
const corners = ref<{ tl: Corner; tr: Corner; br: Corner; bl: Corner }>({
  tl: { x: 0, y: 0 },
  tr: { x: 0, y: 0 },
  br: { x: 0, y: 0 },
  bl: { x: 0, y: 0 },
});

const containerEl = ref<HTMLDivElement | null>(null);
const previewEl = ref<HTMLImageElement | null>(null);
const draggingCorner = ref<keyof typeof corners.value | null>(null);

// Размер preview-изображения в DOM-пикселях (для перевода touch coords в исходные)
const displaySize = ref({ width: 0, height: 0 });

const HANDLE_RADIUS = 22;  // 44px touch target — обязательно для перчаток

onMounted(() => {
  // Стартовые позиции — углы изображения с отступом 10%
  const w = props.sourceCanvas.width;
  const h = props.sourceCanvas.height;
  corners.value = {
    tl: { x: w * 0.1, y: h * 0.1 },
    tr: { x: w * 0.9, y: h * 0.1 },
    br: { x: w * 0.9, y: h * 0.9 },
    bl: { x: w * 0.1, y: h * 0.9 },
  };

  // Конвертируем canvas в data URL для отображения как <img>
  if (previewEl.value) {
    previewEl.value.src = props.sourceCanvas.toDataURL('image/jpeg', 0.92);
  }
});

watch(previewEl, (el) => {
  if (el) {
    el.onload = () => {
      displaySize.value = {
        width: el.clientWidth,
        height: el.clientHeight,
      };
    };
  }
});

const scaleX = computed(() =>
  displaySize.value.width > 0 ? displaySize.value.width / props.sourceCanvas.width : 1
);
const scaleY = computed(() =>
  displaySize.value.height > 0 ? displaySize.value.height / props.sourceCanvas.height : 1
);

// SVG-line через 4 угла — рисуем прямо поверх <img>
const polygonPoints = computed(() => {
  const c = corners.value;
  const pts = [
    [c.tl.x * scaleX.value, c.tl.y * scaleY.value],
    [c.tr.x * scaleX.value, c.tr.y * scaleY.value],
    [c.br.x * scaleX.value, c.br.y * scaleY.value],
    [c.bl.x * scaleX.value, c.bl.y * scaleY.value],
  ];
  return pts.map((p) => p.join(',')).join(' ');
});

function startDrag(which: keyof typeof corners.value, ev: PointerEvent) {
  ev.preventDefault();
  draggingCorner.value = which;
  (ev.target as Element).setPointerCapture(ev.pointerId);
}

function onDrag(ev: PointerEvent) {
  if (!draggingCorner.value || !containerEl.value) return;
  ev.preventDefault();
  const rect = containerEl.value.getBoundingClientRect();
  const offsetX = ev.clientX - rect.left;
  const offsetY = ev.clientY - rect.top;
  // Конвертируем DOM-пиксели в координаты исходного canvas
  const srcX = clamp(offsetX / scaleX.value, 0, props.sourceCanvas.width);
  const srcY = clamp(offsetY / scaleY.value, 0, props.sourceCanvas.height);
  corners.value[draggingCorner.value] = { x: srcX, y: srcY };
}

function endDrag() {
  draggingCorner.value = null;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function confirm() {
  emit('confirmed', {
    topLeftCorner: corners.value.tl,
    topRightCorner: corners.value.tr,
    bottomRightCorner: corners.value.br,
    bottomLeftCorner: corners.value.bl,
  });
}
</script>

<template>
  <div class="fixed inset-0 z-50 bg-black flex flex-col">
    <header class="bg-black/80 text-white p-3 text-center text-sm">
      Передвиньте 4 точки к углам листа и нажмите «Готово»
    </header>

    <!-- Preview + перетаскиваемые углы -->
    <div
      ref="containerEl"
      class="flex-1 relative flex items-center justify-center overflow-hidden touch-none"
      @pointermove="onDrag"
      @pointerup="endDrag"
      @pointercancel="endDrag"
    >
      <img
        ref="previewEl"
        alt="Preview"
        class="max-w-full max-h-full select-none pointer-events-none"
        draggable="false"
      />

      <!-- SVG-обводка контура -->
      <svg
        v-if="displaySize.width > 0"
        :viewBox="`0 0 ${displaySize.width} ${displaySize.height}`"
        :width="displaySize.width"
        :height="displaySize.height"
        class="absolute pointer-events-none"
        :style="{
          left: `calc(50% - ${displaySize.width / 2}px)`,
          top: `calc(50% - ${displaySize.height / 2}px)`,
        }"
      >
        <polygon
          :points="polygonPoints"
          fill="rgba(255, 255, 255, 0.1)"
          stroke="white"
          stroke-width="2"
        />
      </svg>

      <!-- Handles — поверх SVG, кликабельны -->
      <div
        v-for="(_, name) in corners"
        :key="name"
        :style="{
          position: 'absolute',
          left: `calc(50% - ${displaySize.width / 2}px + ${corners[name].x * scaleX - HANDLE_RADIUS}px)`,
          top: `calc(50% - ${displaySize.height / 2}px + ${corners[name].y * scaleY - HANDLE_RADIUS}px)`,
          width: `${HANDLE_RADIUS * 2}px`,
          height: `${HANDLE_RADIUS * 2}px`,
        }"
        class="rounded-full bg-white border-2 border-button shadow-lg cursor-grab active:cursor-grabbing"
        @pointerdown="startDrag(name as 'tl' | 'tr' | 'br' | 'bl', $event)"
      ></div>
    </div>

    <footer class="bg-black/80 p-4 flex gap-2 safe-bottom">
      <button class="btn btn-secondary text-white border-white flex-1" @click="emit('cancelled')">
        Отмена
      </button>
      <button class="btn flex-1" @click="confirm">✅ Готово</button>
    </footer>
  </div>
</template>

<style scoped>
.safe-bottom {
  padding-bottom: max(env(safe-area-inset-bottom), 1rem);
}
</style>
